import { useQueryClient } from "react-query";
import { getLecture, getUserLectureList } from "../../api";
import { useMount, useUpdateEffect } from "react-use";
import { useEffect, useState } from "react";
import { Button, LinkButton } from "../../components/common/Forms";
import Icon from "../../components/common/Icon";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  getItemButtons,
  getStatusBadge,
  useLocationQuery,
} from "../../util/common";
import QuizViewModal from "../../components/quiz/QuizViewModal";
import {
  quizFocusIndex,
  quizInfoContents,
  quizInfoTitle,
  quizList,
} from "../../states/atoms/quizState";
import { useRecoilValue, useSetRecoilState } from "recoil";
import QuizAddModal from "../../components/quiz/QuizAddModal";
import { signItem } from "../../states/atoms/signState";

const QuizListPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const query = useLocationQuery();
  const [{ sort, asc }, setSort] = useState({ sort: null, asc: true });
  const [lectureList, setLectures] = useState([]);
  const [selectedLecture, setSelected] = useState(undefined);
  const [contents, setQueryResult] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, data: null });
  const [addModal, setAddModal] = useState({ open: false, data: null });
  const [auth, setAuth] = useState(false);
  const setTitle = useSetRecoilState(quizInfoTitle);
  const setContents = useSetRecoilState(quizInfoContents);
  const setList = useSetRecoilState(quizList);
  const setFocus = useSetRecoilState(quizFocusIndex);
  const signInfo = useRecoilValue(signItem);

  const handleAddClose = () => {
    setAddModal({ open: false, data: null });
  };

  const handleAdd = () => {
    const modalList = [...lectureList].filter(({ _id }) => _id !== "all");
    modalList.unshift({
      _id: "",
      title: "퀴즈을 추가할 강의를 선택하세요.",
    });
    setAddModal({
      open: true,
      data: {
        lectureList: modalList,
        selectedLecture,
      },
      onAdd: ({ lecture }) => {
        navigate(`/quiz/add/${lecture}`);
        handleAddClose();
      },
      onClose: handleAddClose,
    });
  };

  const handleSelect = (e) => {
    setSelected(e.target.value);
  };

  const handleViewClose = () => {
    setViewModal({ open: false, data: null });
    getListQuery();
  };

  const handleViewUpdate = (id) => {
    navigate(`/quiz/update/${id}`);
    handleViewClose();
  };

  const handleViewDuplicate = (data) => {
    const {
      data: { title, contents, list },
      lecture_id,
    } = data;
    setTitle(title);
    setContents(contents);
    setList(list);
    setFocus(0);
    navigate(`/quiz/add/${lecture_id}`);
  };

  const handleSort = (key) => () => {
    if (sort === key) {
      setSort({ sort, asc: !asc });
    } else {
      setSort({ sort: key, asc: true });
    }
  };

  const getListQuery = async () => {
    const obj =
      selectedLecture === "all" || !selectedLecture
        ? { sort, asc }
        : { lecture_id: selectedLecture, sort, asc };
    const result = await queryClient.fetchQuery(["quiz/list"], {
      queryFn: () => getLecture(obj),
    });
    if (result.result) {
      const data = result.data.reduce((acc, listitem) => {
        const { _id: id, title, start_date: sd, end_date: ed, quiz } = listitem;
        const start_date = new moment(sd).format("YYYY-MM-DD HH:mm");
        const end_date = new moment(ed).format("YYYY-MM-DD HH:mm");
        const modalList = [...lectureList].filter(({ _id }) => _id !== "all");
        modalList.unshift({
          _id: null,
          title: "퀴즈을 추가할 강의를 선택하세요.",
        });
        const qList = quiz.map(
          ({
            _id: qid,
            title: qTitle,
            questions,
            options,
            answers,
            contents,
          }) => {
            return {
              key: `row_${qid}`,
              item: [
                {
                  key: `r${qid}_cell_quiz_title`,
                  value: qid,
                  width: "w-auto",
                  label: (
                    <LinkButton
                      onClick={() =>
                        setViewModal({
                          open: true,
                          auth,
                          data: {
                            id: qid,
                            title: qTitle,
                            questions,
                            options,
                            answers,
                            contents,
                            lectureList: modalList,
                          },
                          onClose: handleViewClose,
                          onDuplicate: handleViewDuplicate,
                          onUpdate: handleViewUpdate,
                        })
                      }
                    >
                      {qTitle}
                    </LinkButton>
                  ),
                },
                {
                  key: `r${id}_cell_title`,
                  value: id,
                  width: "w-auto",
                  label: title,
                },
                {
                  ...getStatusBadge(start_date, end_date),
                  key: `r${id}_cell_status`,
                  width: "w-auto text-center",
                },
                {
                  ...getItemButtons({
                    item: "quiz",
                    data: { _id: qid, lecture_id: id },
                    id,
                  }),
                  key: `r${id}_cell_quiz_config`,
                  width: "w-auto text-center",
                },
              ],
            };
          }
        );

        return [...acc, ...qList];
      }, []);
      setQueryResult(data);
    }
  };

  useEffect(() => {
    if (signInfo && "info" in signInfo) {
      if (signInfo.info && "role" in signInfo.info) {
        const { role } = signInfo.info;
        return setAuth(role === "admin");
      }
    }
    setAuth(false);
  }, [signInfo]);

  useEffect(() => {
    setSelected(query.lecture);
  }, [query.lecture]);

  useUpdateEffect(() => {
    getListQuery();
  }, [selectedLecture, sort, asc]);

  useMount(async () => {
    const result = await queryClient.fetchQuery(["quiz/lecture/list"], {
      queryFn: getUserLectureList,
    });
    if (result.result) {
      const arr = [...result.data];
      arr.unshift({ _id: "all", title: "전체보기" });
      setLectures(arr);
    }
  });

  return (
    <div className="w-full mx-auto md:w-11/12 md:px-2">
      <h1 className="flex items-center font-sans font-bold break-normal text-cyan-500 px-2 py-4 text-xl md:text-2xl">
        퀴즈 관리
      </h1>
      <div className="p-4 md:p-8 lg:mt-0 rounded shadow bg-white">
        <div className="w-full flex gap-2 md:flex-row flex-col justify-between md:items-center mb-2 sticky top-0 left-0 right-0">
          <div className="inline-flex w-full md:w-1/2 items-center">
            <label
              htmlFor="classes"
              className="w-20 block text-sm font-medium text-gray-700"
            >
              강의 선택
            </label>
            <select
              id="classes"
              name="classes"
              autoComplete="classes-name"
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              onChange={handleSelect}
              value={selectedLecture}
            >
              {lectureList.map(({ _id: id, title: v }) => (
                <option key={`option_${v}`} value={id}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          {auth && (
            <Button
              type="primary"
              size="sm"
              className="self-end w-1/2 md:w-auto"
              onClick={handleAdd}
            >
              <Icon name="plus" size="sm" />
              퀴즈 추가
            </Button>
          )}
        </div>
        <div className="rounded overflow-scroll scrollbar-hide">
          <table className="stripe rounded hover min-w-full w-max py-4 text-sm table-auto border-collapse border border-zinc-400">
            <thead className="rounded-t">
              <tr className="cursor-default">
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("quiz.title")}
                >
                  <div className="inline-flex items-center">
                    퀴즈
                    {sort === "quiz.title" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("title")}
                >
                  <div className="inline-flex items-center">
                    강의
                    {sort === "title" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("status")}
                >
                  <div className="inline-flex items-center">
                    상태
                    {sort === "status" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th className="w-auto py-2 border border-zinc-200 bg-zinc-100">
                  <div className="inline-flex items-center">관리</div>
                </th>
              </tr>
            </thead>
            <tbody className="rounded-b">
              {contents.length > 0 &&
                contents.map(({ key, item }) => (
                  <tr key={key}>
                    {item.map(({ key, width, label }) => (
                      <td
                        {...{
                          key,
                          className: `border border-zinc-300 p-1 ${width}`,
                        }}
                      >
                        {label}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <QuizViewModal {...{ ...viewModal }} />
      <QuizAddModal {...{ ...addModal }} />
    </div>
  );
};

export default QuizListPage;
